// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Dør / Vindue / Åbning tools and the edits of a placed opening
 * (frontend-u0o.5, slice S5; design.md §4, mock 03 — Revit's hosted
 * families):
 *
 *  - an opening lives on a host wall, between its neighbours: the host's
 *    ends, the walls that meet or cross it (their faces) and its other
 *    openings (`hostObstacles`);
 *  - placing centres it on the pointer's projection onto the host, slid
 *    along to fit the free stretch it is in (or centred there, Centrér), or
 *    at a typed distance from the neighbour on either side. It is refused —
 *    in Danish, with the numbers — when the pointer is on another opening,
 *    when the free stretch is too short, or when it reaches over the wall;
 *  - a door swings to the side the pointer is on (Space flips it) and hangs
 *    on the jamb the hand (V / H, from the swing side) says;
 *  - Revit's temporary dimensions: the gaps to the neighbours and the width,
 *    drawn on the side away from the swing;
 *  - edits: slide along the host (or drop on another wall: re-host), a jamb
 *    dragged (width), a typed gap or width, flip facing / hand.
 *
 * Every result is `POST /model/edits` ops (`opening.create` / `.update`).
 * World x/y, heights world z from the host's base. Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidOpening, SolidWall } from '../api/types';
import { findElement, type GeometriModel } from './geometri';
import { elementTitle, lengthInput } from './geometriEdit';
import { lineIntersection, nearestOnSegment } from './inference';
import { hingeFor, otherHinge, otherSide, readSwing, swingPatch, swingRecord, type Hand, type Hinge, type SwingSide } from './openingSwing';
import type { XY } from './planWalls';
import { alignAlong, type Track, type TrackPoint } from './smartGuides.ts';

export type OpeningKind = SolidOpening['kind'];

export interface OpeningPreset {
  id: string;
  label: string;
  width: number;
  height: number;
  /** Above the host's base, metres. */
  sill: number;
  leaves: 1 | 2;
}

/** The types the options strip offers per tool (the strip's ▾). */
export const OPENING_PRESETS: Record<OpeningKind, readonly OpeningPreset[]> = {
  door: [
    { id: 'door-80', label: 'Enkeltdør 80', width: 0.8, height: 2.1, sill: 0, leaves: 1 },
    { id: 'door-90', label: 'Enkeltdør 90', width: 0.9, height: 2.1, sill: 0, leaves: 1 },
    { id: 'door-100', label: 'Enkeltdør 100', width: 1.0, height: 2.1, sill: 0, leaves: 1 },
    { id: 'door-120', label: 'Dobbeltdør 120', width: 1.2, height: 2.1, sill: 0, leaves: 2 },
    { id: 'door-160', label: 'Dobbeltdør 160', width: 1.6, height: 2.1, sill: 0, leaves: 2 },
  ],
  window: [
    { id: 'window-60', label: 'Vindue 60 × 120', width: 0.6, height: 1.2, sill: 0.9, leaves: 1 },
    { id: 'window-120', label: 'Vindue 120 × 120', width: 1.2, height: 1.2, sill: 0.9, leaves: 1 },
    { id: 'window-180', label: 'Vindue 180 × 130', width: 1.8, height: 1.3, sill: 0.8, leaves: 1 },
    { id: 'window-door', label: 'Altandør 90', width: 0.9, height: 2.1, sill: 0, leaves: 1 },
  ],
  opening: [
    { id: 'opening-90', label: 'Åbning 90', width: 0.9, height: 2.1, sill: 0, leaves: 1 },
    { id: 'opening-120', label: 'Åbning 120', width: 1.2, height: 2.1, sill: 0, leaves: 1 },
    { id: 'opening-hatch', label: 'Lem 60 × 60', width: 0.6, height: 0.6, sill: 1.0, leaves: 1 },
  ],
};

const DEFAULT_PRESET: Record<OpeningKind, string> = { door: 'door-90', window: 'window-120', opening: 'opening-90' };

/** The options strip of one tool: the type, its measures (typed over), and a door's hand. */
export interface OpeningOptions {
  /** The preset the measures came from; null once a measure is typed over. */
  presetId: string | null;
  width: number;
  height: number;
  sill: number;
  leaves: 1 | 2;
  /** The hinge as it reads from the swing side (doors). */
  hand: Hand;
  /** Centrér: in the middle of the free stretch the pointer is in. */
  centre: boolean;
}

export function presetById(kind: OpeningKind, id: string | null): OpeningPreset | null {
  return OPENING_PRESETS[kind].find((p) => p.id === id) ?? null;
}

export function defaultOpeningOptions(kind: OpeningKind): OpeningOptions {
  const p = presetById(kind, DEFAULT_PRESET[kind])!;
  return { presetId: p.id, width: p.width, height: p.height, sill: p.sill, leaves: p.leaves, hand: 'H', centre: false };
}

/** The type name an opening is created with: the preset's, or none once its measures are typed over. */
export function optionsTypeName(kind: OpeningKind, o: OpeningOptions): string | null {
  return presetById(kind, o.presetId)?.label ?? null;
}

const KIND_NOUN: Record<OpeningKind, string> = { door: 'dør', window: 'vindue', opening: 'åbning' };

// ------------------------------------------------------------ geometry --

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6 + 0;
const m = (v: number) => `${lengthInput(v)} m`;
/** Two lengths, told apart: millimetres when centimetres would read the same ("0,898 m", "0,900 m"). */
const m2 = (a: number, b: number): [string, string] => (lengthInput(a) === lengthInput(b) ? [`${a.toFixed(3).replace('.', ',')} m`, `${b.toFixed(3).replace('.', ',')} m`] : [m(a), m(b)]);
const EPS = 1e-6;

/** Unit direction, left normal and length of a host's axis; null for one of no length. */
export function hostFrame(w: Pick<SolidWall, 'start' | 'end'>): { dir: XY; normal: XY; length: number } | null {
  const d = sub(w.end, w.start);
  const L = Math.hypot(d[0], d[1]);
  if (!(L > 1e-9)) return null;
  const dir: XY = [d[0] / L, d[1] / L];
  return { dir, normal: [-dir[1], dir[0]], length: L };
}

/** Where `p` falls along `host`'s axis (metres from its start), and on which side. */
export function alongHost(host: SolidWall, p: XY): { s: number; side: SwingSide } | null {
  const f = hostFrame(host);
  if (!f) return null;
  const v = sub(p, host.start);
  return { s: dot(v, f.dir), side: dot(v, f.normal) >= 0 ? 'a' : 'b' };
}

/** A point at `s` along `host`'s axis, `off` metres to its left. */
export function onHost(host: SolidWall, s: number, off = 0): XY {
  const f = hostFrame(host)!;
  return add(add(host.start, mul(f.dir, s)), mul(f.normal, off));
}

/** Something an opening cannot overlap, as a stretch of the host's axis. */
export interface HostObstacle {
  lo: number;
  hi: number;
  kind: 'end' | 'wall' | 'opening';
  guid: string | null;
  /** "Væg 5", "Vindue V7", "vægenden". */
  label: string;
}

/** Below this sine (~10°) a wall runs along the host rather than meeting it. */
const MIN_SINE = Math.sin((10 * Math.PI) / 180);
/** Slack (metres) for a wall to count as meeting the host. */
const MEET_SLACK = 0.03;

/**
 * The host's ends, the walls on its storey that meet or cross it (the
 * stretch their body covers, face to face) and its openings (but `exclude`),
 * sorted along the host.
 */
export function hostObstacles(model: GeometriModel, host: SolidWall, exclude: string | null = null): HostObstacle[] {
  const f = hostFrame(host);
  if (!f) return [];
  const L = f.length;
  const half = (host.thickness ?? 0) / 2;
  const out: HostObstacle[] = [
    { lo: -Infinity, hi: 0, kind: 'end', guid: null, label: 'vægenden' },
    { lo: L, hi: Infinity, kind: 'end', guid: null, label: 'vægenden' },
  ];
  for (const w of model.walls ?? []) {
    if (w.guid === host.guid || w.storey_id !== host.storey_id) continue;
    const wf = hostFrame(w);
    if (!wf) continue;
    const sine = Math.abs(crs(f.dir, wf.dir));
    if (sine < MIN_SINE) continue;
    const x = lineIntersection(host.start, f.dir, w.start, sub(w.end, w.start));
    if (!x) continue;
    // It reaches the host's body (a tee ends on its axis or face; a cross goes through) …
    if (dist(nearestOnSegment(w.start, w.end, x).point, x) > half / sine + MEET_SLACK) continue;
    const s = dot(sub(x, host.start), f.dir);
    const reach = ((w.thickness ?? 0) / 2) / sine;
    // … within the host's span.
    if (s + reach < 0 || s - reach > L) continue;
    out.push({ lo: s - reach, hi: s + reach, kind: 'wall', guid: w.guid, label: `Væg ${w.id}` });
  }
  for (const o of model.openings ?? []) {
    if (o.wall_guid !== host.guid || o.guid === exclude) continue;
    out.push({ lo: Math.min(o.s_start, o.s_end), hi: Math.max(o.s_start, o.s_end), kind: 'opening', guid: o.guid, label: elementTitle({ kind: 'opening', row: o }) });
  }
  return out.sort((a, b) => a.lo - b.lo || a.hi - b.hi);
}

/** What to place: the kind and its measures (metres; `sill` above the host's base). */
export interface PlaceSpec {
  kind: OpeningKind;
  width: number;
  height: number;
  sill: number;
}

export interface PlaceOptions {
  /** The swing side: the pointer's (Revit) or a given one (an edit keeps the door's). */
  side: SwingSide | 'pointer';
  /** Space: the other side. */
  flip: boolean;
  /** The hand (a door's hinge as it reads from the swing side); an edit passes `hinge` instead. */
  hand?: Hand;
  hinge?: Hinge;
  centre: boolean;
  /** A typed distance from the neighbour on one side (the measurement box). */
  typed?: { from: 'left' | 'right'; distance: number };
  /** The opening being moved: not its own neighbour. */
  exclude?: string | null;
  /** Keep the pointer's position to the millimetre (a smart guide aligned it): no rounding of the gaps to the centimetre. */
  exact?: boolean;
}

export interface Placement {
  host: SolidWall;
  s0: number;
  s1: number;
  side: SwingSide;
  hinge: Hinge;
  /** The neighbours either side (null: none within the host — never, the ends count). */
  left: HostObstacle;
  right: HostObstacle;
  gapLeft: number;
  gapRight: number;
  /** World z. */
  z_sill: number;
  z_head: number;
}

export type PlaceResult = { ok: Placement } | { error: string };

/** Openings narrower than this (metres) are no opening. */
export const MIN_WIDTH = 0.3;
/** Placement rounds its gaps to the centimetre. */
const STEP = 0.01;
const snap = (v: number) => Math.round(v / STEP) * STEP;

/**
 * Opening `spec` on `host` at plan point `p` (see the module comment): its
 * stretch, swing and neighbours, or the reason it cannot go there.
 */
export function placeOpening(model: GeometriModel, host: SolidWall, p: XY, spec: PlaceSpec, opts: PlaceOptions): PlaceResult {
  const at = alongHost(host, p);
  if (!at) return { error: 'Væggen har ingen længde' };
  const w = spec.width;
  if (!(w >= MIN_WIDTH - EPS)) return { error: `For smal: mindst ${m(MIN_WIDTH)}` };
  const hostHeight = host.z_top - host.z_base;
  if (spec.sill + spec.height > hostHeight + 0.005) return { error: `For høj: overkant ${m(spec.sill + spec.height)} over væggens ${m(hostHeight)}` };
  const obs = hostObstacles(model, host, opts.exclude ?? null);
  let c = at.s;
  const inside = obs.find((o) => o.lo < c - EPS && c + EPS < o.hi);
  if (inside?.kind === 'opening') return { error: `Overlapper ${inside.label}` };
  if (inside) {
    // On a meeting wall (or past an end): step out to the nearer side.
    c = c - inside.lo < inside.hi - c ? inside.lo - EPS : inside.hi + EPS;
  }
  // The free stretch around c.
  let left = obs[0];
  let right = obs[obs.length - 1];
  for (const o of obs) {
    if (o.hi <= c + EPS && o.hi >= left.hi) left = o;
    if (o.lo >= c - EPS && o.lo <= right.lo) right = o;
  }
  const lo = left.hi;
  const hi = right.lo;
  const free = hi - lo;
  const noun = KIND_NOUN[spec.kind];
  if (free < w - EPS) {
    if (opts.typed) return { error: `Passer ikke: kun ${m(Math.max(0, free - w))} til ${right.label}` };
    const [mw, mf] = m2(w, Math.max(0, free));
    return { error: `Passer ikke: ${noun} ${mw} — kun ${mf} fri væg her` };
  }
  let s0: number;
  if (opts.typed) {
    const d = opts.typed.distance;
    if (!(d >= 0)) return { error: 'Afstanden kan ikke være negativ' };
    const room = free - w;
    if (d > room + EPS) {
      const other = opts.typed.from === 'left' ? right : left;
      return { error: `Passer ikke: kun ${m(room)} til ${other.label}` };
    }
    s0 = opts.typed.from === 'left' ? lo + d : hi - d - w;
  } else if (opts.centre) s0 = (lo + hi) / 2 - w / 2;
  else {
    s0 = opts.exact ? c - w / 2 : lo + snap(c - w / 2 - lo);
    s0 = Math.min(Math.max(s0, lo), hi - w);
  }
  const s1 = s0 + w;
  const pointerSide = opts.side === 'pointer' ? at.side : opts.side;
  const side = opts.flip ? otherSide(pointerSide) : pointerSide;
  const hinge = opts.hinge ?? hingeFor(side, opts.hand ?? 'H');
  return {
    ok: {
      host,
      s0: r6(s0),
      s1: r6(s1),
      side,
      hinge,
      left,
      right,
      gapLeft: r6(s0 - lo),
      gapRight: r6(hi - s1),
      z_sill: r6(host.z_base + spec.sill),
      z_head: r6(host.z_base + spec.sill + spec.height),
    },
  };
}

/**
 * How far (metres, signed along the host's left normal) the dimension line
 * runs from the axis: past the face away from the swing, by 0,35 m or — at a
 * zoom where that is a few pixels — by the caller's `minOffset`.
 */
export function dimOffset(p: Placement, minOffset = 0): number {
  const half = (p.host.thickness ?? 0) / 2;
  return (p.side === 'a' ? -1 : 1) * Math.max(half + 0.35, minOffset);
}

/** A temporary dimension of a placement: world x/y, its figure at `at`. */
export interface OpeningDim {
  id: 'left' | 'width' | 'right';
  from: XY;
  to: XY;
  at: XY;
  value: number;
}

/**
 * The gaps to the neighbours and the width, one chain along the host's face
 * away from the swing (Revit draws them where the leaf is not). A neighbour at the
 * host's end is measured to the end.
 */
export function placementDims(p: Placement, minOffset = 0): OpeningDim[] {
  const off = dimOffset(p, minOffset);
  const L = hostFrame(p.host)?.length ?? 0;
  const lo = Number.isFinite(p.left.hi) ? p.left.hi : 0;
  const hi = Number.isFinite(p.right.lo) ? p.right.lo : L;
  const dim = (id: OpeningDim['id'], a: number, b: number, k = off): OpeningDim => ({
    id,
    from: onHost(p.host, a, k),
    to: onHost(p.host, b, k),
    at: onHost(p.host, (a + b) / 2, k),
    value: r6(b - a),
  });
  // One chain, as Revit draws it: gap, width, gap (the width's figure never sits on the grips).
  return [dim('left', lo, p.s0), dim('width', p.s0, p.s1), dim('right', p.s1, hi)];
}

// ----------------------------------------------------------------- ops --

/** The `opening.create` for a placement (one batch, one undo). */
export function createOpeningOps(p: Placement, spec: PlaceSpec, guid: string, typeName: string | null, leaves: 1 | 2 = 1): ModelEditOp[] {
  const leaf = swingRecord({ side: p.side, hinge: p.hinge, leaves, typeName }, spec.kind === 'door');
  return [
    {
      op: 'opening.create',
      record: {
        guid,
        wall_guid: p.host.guid,
        kind: spec.kind,
        state: 'closed',
        s_start: p.s0,
        s_end: p.s1,
        z_sill: p.z_sill,
        z_head: p.z_head,
        is_external: p.host.is_external,
        ...leaf,
      },
    },
  ];
}

function openingAndHost(model: GeometriModel, guid: string): { o: SolidOpening; host: SolidWall } | null {
  const f = findElement(model, guid);
  if (f?.kind !== 'opening') return null;
  const host = model.walls?.find((w) => w.guid === f.row.wall_guid);
  return host ? { o: f.row, host } : null;
}

const specOf = (o: SolidOpening, host: SolidWall): PlaceSpec => ({
  kind: o.kind,
  width: Math.abs(o.s_end - o.s_start),
  height: o.z_head - o.z_sill,
  sill: o.z_sill - host.z_base,
});

/** The placement a selected opening has now (its gaps, for the temporary dimensions). */
export function selectedPlacement(model: GeometriModel, guid: string): Placement | null {
  const oh = openingAndHost(model, guid);
  if (!oh) return null;
  const { o, host } = oh;
  const sw = readSwing(o);
  const lo = Math.min(o.s_start, o.s_end);
  const hi = Math.max(o.s_start, o.s_end);
  const obs = hostObstacles(model, host, guid);
  let left = obs[0];
  let right = obs[obs.length - 1];
  const c = (lo + hi) / 2;
  for (const x of obs) {
    if (x.hi <= c + EPS && x.hi >= left.hi) left = x;
    if (x.lo >= c - EPS && x.lo <= right.lo) right = x;
  }
  return { host, s0: lo, s1: hi, side: sw.side, hinge: sw.hinge, left, right, gapLeft: r6(lo - left.hi), gapRight: r6(right.lo - hi), z_sill: o.z_sill, z_head: o.z_head };
}

export type OpeningEdit = { ops: ModelEditOp[]; label: string } | { error: string };

/**
 * Opening `guid` dragged so its middle is at plan point `p` on wall
 * `target` (its own host: a slide; another: a re-host). Its width, heights
 * over the base and swing on the same world side are kept (a host across the
 * old one takes the pointer's side).
 */
export function moveOpeningOps(model: GeometriModel, guid: string, p: XY, target: SolidWall, exact = false): OpeningEdit {
  const oh = openingAndHost(model, guid);
  if (!oh) return { error: 'Åbningen findes ikke' };
  const { o, host } = oh;
  const sw = readSwing(o);
  const rehost = target.guid !== host.guid;
  let side: SwingSide | 'pointer' = sw.side;
  let hinge = sw.hinge;
  if (rehost) {
    const fo = hostFrame(host);
    const fn = hostFrame(target);
    if (!fo || !fn) return { error: 'Væggen har ingen længde' };
    const worldN = sw.side === 'a' ? fo.normal : mul(fo.normal, -1);
    const along = dot(worldN, fn.normal);
    side = Math.abs(along) >= 0.5 ? (along > 0 ? 'a' : 'b') : 'pointer';
    const worldH = sw.hinge === 'start' ? mul(fo.dir, -1) : fo.dir;
    const h = dot(worldH, fn.dir);
    if (Math.abs(h) >= 0.5) hinge = h < 0 ? 'start' : 'end';
  }
  const spec = specOf(o, host);
  // Over the new host's base, the same height up as on the old.
  const r = placeOpening(model, target, p, { ...spec, sill: o.z_sill - host.z_base }, { side, flip: false, hinge, centre: false, exclude: guid, exact });
  if ('error' in r) return r;
  const set: Record<string, unknown> = { s_start: r.ok.s0, s_end: r.ok.s1 };
  if (rehost) {
    set.wall_guid = target.guid;
    if (target.z_base !== host.z_base) {
      set.z_sill = r.ok.z_sill;
      set.z_head = r.ok.z_head;
    }
    if (o.kind === 'door' && (r.ok.side !== sw.side || r.ok.hinge !== sw.hinge || !sw.known)) Object.assign(set, swingPatch(o, { side: r.ok.side, hinge: r.ok.hinge }));
  }
  const title = elementTitle({ kind: 'opening', row: o });
  if (!rehost && Math.abs(r.ok.s0 - Math.min(o.s_start, o.s_end)) < EPS) return { ops: [], label: `Flyt ${title}` };
  return { ops: [{ op: 'opening.update', guid, set }], label: rehost ? `Flyt ${title} til Væg ${target.id}` : `Flyt ${title}` };
}

/** Jamb `jamb` (the lower-s end is `start`) of opening `guid` dragged to plan point `p`: its new width, up to the neighbour. */
export function resizeOpeningOps(model: GeometriModel, guid: string, jamb: 'start' | 'end', p: XY): OpeningEdit {
  const oh = openingAndHost(model, guid);
  if (!oh) return { error: 'Åbningen findes ikke' };
  const { o, host } = oh;
  const at = alongHost(host, p);
  const cur = selectedPlacement(model, guid);
  if (!at || !cur) return { error: 'Væggen har ingen længde' };
  let s0 = cur.s0;
  let s1 = cur.s1;
  if (jamb === 'start') s0 = Math.max(cur.left.hi, snap(at.s));
  else s1 = Math.min(cur.right.lo, snap(at.s));
  if (s1 - s0 < MIN_WIDTH - EPS) return { error: `For smal: mindst ${m(MIN_WIDTH)}` };
  const title = elementTitle({ kind: 'opening', row: o });
  return { ops: [{ op: 'opening.update', guid, set: { s_start: r6(s0), s_end: r6(s1) } }], label: `Bredde · ${title}` };
}

/**
 * A temporary dimension of a selected opening typed: the gap to the left or
 * right neighbour (it slides), or its width (from its left jamb, or its right
 * one when there is no room). Null when it does not fit or nothing changes.
 */
export function gapOps(model: GeometriModel, guid: string, which: OpeningDim['id'], value: number): ModelEditOp[] | null {
  const p = selectedPlacement(model, guid);
  if (!p || !(value >= 0)) return null;
  const lo = p.left.hi;
  const hi = p.right.lo;
  const w = p.s1 - p.s0;
  let s0 = p.s0;
  let s1 = p.s1;
  if (which === 'left') {
    s0 = lo + value;
    s1 = s0 + w;
  } else if (which === 'right') {
    s1 = hi - value;
    s0 = s1 - w;
  } else {
    if (value < MIN_WIDTH - EPS) return null;
    s1 = s0 + value;
    if (s1 > hi + EPS) {
      s1 = hi;
      s0 = hi - value;
    }
  }
  if (s0 < lo - EPS || s1 > hi + EPS) return null;
  if (Math.abs(s0 - p.s0) < EPS && Math.abs(s1 - p.s1) < EPS) return null;
  return [{ op: 'opening.update', guid, set: { s_start: r6(s0), s_end: r6(s1) } }];
}

/** Flip a door's facing (`side`, Space) or hand (`hinge`, H). Nothing for a window or an opening. */
export function flipOpeningOps(model: GeometriModel, guid: string, which: 'side' | 'hinge'): ModelEditOp[] {
  const f = findElement(model, guid);
  if (f?.kind !== 'opening' || f.row.kind !== 'door') return [];
  const sw = readSwing(f.row);
  const patch = which === 'side' ? { side: otherSide(sw.side) } : { hinge: otherHinge(sw.hinge) };
  return [{ op: 'opening.update', guid, set: swingPatch(f.row, patch) }];
}

// ------------------------------------------------------ server warnings --

/**
 * A warning of `POST /model/edits` (ruxd #32: the opening fit check, ReUseX
 * core/opening_fit.cpp — an opening is applied where it was put, then
 * warned about) in Danish, the elements by their titles in `model`. Any
 * other warning is kept as the server said it.
 */
export function warningText(text: string, model: GeometriModel): string {
  const title = (guid: string) => {
    const f = findElement(model, guid);
    return f ? elementTitle(f) : 'en åbning';
  };
  const wall = (guid: string) => {
    const w = model.walls?.find((x) => x.guid === guid);
    return w ? `Væg ${w.id}` : 'væggen';
  };
  const len = (v: string) => m(Number(v));
  let x = /^opening (\S+) overlaps opening (\S+) by ([\d.]+) m on wall (\S+)/.exec(text);
  if (x) return `${title(x[1])} overlapper ${title(x[2])} med ${len(x[3])} på ${wall(x[4])}`;
  x = /^opening (\S+) runs ([\d.]+) m past the end of wall (\S+)/.exec(text);
  if (x) return `${title(x[1])} går ${len(x[2])} ud over enden af ${wall(x[3])}`;
  x = /^opening (\S+) starts ([\d.]+) m before the start of wall (\S+)/.exec(text);
  if (x) return `${title(x[1])} går ${len(x[2])} ud over starten af ${wall(x[3])}`;
  x = /^opening (\S+) has its head ([\d.]+) m above the top of wall (\S+)/.exec(text);
  if (x) return `${title(x[1])} når ${len(x[2])} over toppen af ${wall(x[3])}`;
  x = /^opening (\S+) has its sill ([\d.]+) m below the base of wall (\S+)/.exec(text);
  if (x) return `${title(x[1])} har brystningen ${len(x[2])} under foden af ${wall(x[3])}`;
  return text;
}

// ------------------------------------------------------- smart guides --

/**
 * The smart guides' points an opening on the plan aligns with
 * (frontend-u0o.12): the ends of the storey's walls and the jambs of its
 * other openings (all but `exclude`, the one being moved).
 */
export function openingTrackPoints(model: GeometriModel, storeyId: number | null, exclude: string | null): TrackPoint[] {
  const out: TrackPoint[] = [];
  const walls = (model.walls ?? []).filter((w) => storeyId === null || w.storey_id === storeyId);
  for (const w of walls) {
    out.push({ p: w.start, what: 'endepunkt', of: `Væg ${w.id}`, auto: true }, { p: w.end, what: 'endepunkt', of: `Væg ${w.id}`, auto: true });
  }
  for (const o of model.openings ?? []) {
    if (o.guid === exclude) continue;
    const h = walls.find((w) => w.guid === o.wall_guid);
    if (!h || !hostFrame(h)) continue;
    const of = elementTitle({ kind: 'opening', row: o });
    out.push({ p: onHost(h, o.s_start), what: 'karm', of, auto: true }, { p: onHost(h, o.s_end), what: 'karm', of, auto: true });
  }
  return out;
}

/**
 * An opening `width` wide slid along `host` with the pointer at `raw`:
 * the pointer moved along the host so its middle or a jamb is square
 * across from one of `points`, within `tolerance` — its distance off the
 * axis (the swing side) kept — and the dotted line that shows it.
 */
export function alignOpening(host: SolidWall, raw: XY, width: number, points: readonly TrackPoint[], tolerance: number): { p: XY; tip: string; track: Track } | null {
  const f = hostFrame(host);
  const at = alongHost(host, raw);
  if (!f || !at) return null;
  const r = alignAlong(host.start, f.dir, at.s, [0, -width / 2, width / 2], points, tolerance);
  if (!r) return null;
  return { p: onHost(host, r.s, dot(sub(raw, host.start), f.normal)), tip: r.tip, track: r.track };
}
